import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAsync, useSession, useToast } from '../lib/store';
import type { AppNotification, BreedingSettings, CollectionCard, Profile } from '../lib/types';
import { Button, EmptyState, Panel } from '../components/ui';

import { timeAgo } from '../lib/format';

const BACKGROUNDS = [
  { key: 'meadow', label: 'Meadow' },
  { key: 'dusk', label: 'Dusk' },
  { key: 'cavern', label: 'Cavern' },
  { key: 'shore', label: 'Shore' },
  { key: 'library', label: 'Library' },
];

export function SettingsPage() {
  const { me, refresh } = useSession();
  const { push } = useToast();

  const [trainerName, setTrainerName] = useState('');
  const [bio, setBio] = useState('');
  const [title, setTitle] = useState('');
  const [background, setBackground] = useState('meadow');
  const [avatarSpeciesId, setAvatarSpeciesId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const titles = useAsync<{ titles: Array<{ title: string; from: string }> }>(() => api.get('/me/titles'));
  // The Collection card DTO, not a full Pokemon: this picker needs a species
  // id, a name and a sprite, and `/pokemon` deliberately stopped sending base
  // stats and flavour text for every row when the Collection was redesigned.
  const owned = useAsync<{ pokemon: CollectionCard[] }>(() =>
    api.get('/pokemon', { sort: 'dex', pageSize: 60 }),
  );

  useEffect(() => {
    if (!me) return;
    setTrainerName(me.profile.trainerName);
    setBio(me.profile.bio);
    setTitle(me.profile.title);
    setBackground(me.profile.background);
    setAvatarSpeciesId(me.profile.avatarSpeciesId);
  }, [me?.profile.userId]);

  async function save() {
    setSaving(true);
    try {
      await api.patch<{ profile: Profile }>('/me/profile', {
        trainerName,
        bio,
        title,
        background,
        avatarSpeciesId,
      });
      push('success', 'Profile saved.');
      void refresh();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  // One card per distinct species the player owns, preferring a normal-coloured
  // one: the avatar is the *species*, so previewing it with a shiny sprite
  // would show a picture the profile then does not draw.
  const avatarChoices = (owned.data?.pokemon ?? []).filter((pokemon, index, list) => {
    const best = list.find((other) => other.speciesId === pokemon.speciesId && !other.isShiny)
      ?? list.find((other) => other.speciesId === pokemon.speciesId);
    return best === list[index];
  });

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Settings</h1>
          <div className="page-head__sub">How other trainers see you.</div>
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', alignItems: 'start' }}>
        <Panel title="◈ Profile">
          <div className="stack">
            <div className="field">
              <label className="field__label" htmlFor="tname">Trainer name</label>
              <input
                id="tname"
                className="input"
                value={trainerName}
                maxLength={24}
                onChange={(event) => setTrainerName(event.target.value)}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="bio">
                Bio <span className="muted">({bio.length}/280)</span>
              </label>
              <textarea
                id="bio"
                className="textarea"
                value={bio}
                maxLength={280}
                onChange={(event) => setBio(event.target.value)}
                placeholder="Tell other trainers what you're collecting..."
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="title">Title</label>
              <select id="title" className="select" value={title} onChange={(event) => setTitle(event.target.value)}>
                {(titles.data?.titles ?? [{ title, from: '' }]).map((option) => (
                  <option key={option.title} value={option.title}>
                    {option.title}
                    {option.from ? ` — ${option.from}` : ''}
                  </option>
                ))}
              </select>
              <span className="tiny muted">Titles are unlocked by achievements.</span>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="bg">Profile background</label>
              <select id="bg" className="select" value={background} onChange={(event) => setBackground(event.target.value)}>
                {BACKGROUNDS.map((option) => (
                  <option key={option.key} value={option.key}>{option.label}</option>
                ))}
              </select>
            </div>

            <Button variant="go" onClick={() => void save()} loading={saving}>
              Save profile
            </Button>
          </div>
        </Panel>

        <Panel title="◓ Avatar" hint="Pick from Pokémon you own">
          {avatarChoices.length === 0 ? (
            <EmptyState art="◓" title="Hatch a Pokémon first" />
          ) : (
            <div
              className="grid"
              style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))', maxHeight: 300, overflowY: 'auto', gap: 6 }}
            >
              {avatarChoices.map((pokemon) => (
                <button
                  key={pokemon.speciesId}
                  type="button"
                  className="pxwell"
                  style={{
                    padding: 4,
                    border: 0,
                    cursor: 'pointer',
                    outline: avatarSpeciesId === pokemon.speciesId ? '3px solid var(--leaf)' : 'none',
                    outlineOffset: 2,
                  }}
                  onClick={() => setAvatarSpeciesId(pokemon.speciesId)}
                  title={pokemon.speciesName}
                >
                  <img className="pixel" src={pokemon.sprite} alt={pokemon.speciesName} style={{ width: 56, height: 56, margin: '0 auto' }} />
                </button>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <BreedingPanel />
      <AccountPanel />
      <NotificationsPanel />
    </div>
  );
}

/**
 * Breeding preferences — design PART 29 and PART 48.
 *
 * The auto-locks default **on** and this panel is the only place they can be
 * turned off. The asymmetry is deliberate: a player who wanted to release a
 * Shiny clicks once more, a player who mass-released one has lost something the
 * game cannot give back. Advanced Breeder Mode defaults **off** for the mirror
 * reason — the numbers it reveals are the ones a casual player is explicitly
 * not meant to be shown.
 */
function BreedingPanel() {
  const { push } = useToast();
  const state = useAsync<{ settings: BreedingSettings }>(() => api.get('/breeder/settings'));
  const [busy, setBusy] = useState(false);

  const settings = state.data?.settings;
  if (!settings) return null;

  async function set(patch: Partial<BreedingSettings>) {
    setBusy(true);
    try {
      await api.patch('/breeder/settings', patch);
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  }

  // Typed as the boolean keys only, so a numeric setting cannot be rendered
  // as a checkbox by a later edit.
  type LockKey = 'autoLockShiny' | 'autoLockRareForm' | 'autoLockRareMarking' | 'autoLockExtremeSize' | 'autoLockBloodline';
  const locks: Array<[LockKey, string, string]> = [
    ['autoLockShiny', 'Shiny', 'Never releasable by accident.'],
    ['autoLockRareMarking', 'Rare markings', 'Star, Heart and Crescent.'],
    ['autoLockExtremeSize', 'Extreme sizes', 'Titans and Miniatures.'],
    ['autoLockRareForm', 'Rare forms', 'Official variant forms.'],
    ['autoLockBloodline', 'Bloodline members', 'Anything in one of your registered lines.'],
  ];

  return (
    <Panel title="❦ Breeding" hint="Applied the moment an egg hatches">
      <div className="stack-sm">
        {locks.map(([key, label, help]) => (
          <label key={key} className="row row--between" style={{ gap: 'var(--gap-sm)' }}>
            <span>
              <strong className="tiny">Keep {label}</strong>
              <div className="tiny muted">{help}</div>
            </span>
            <input
              type="checkbox"
              checked={Boolean(settings[key])}
              disabled={busy}
              onChange={(event) => void set({ [key]: event.target.checked })}
            />
          </label>
        ))}

        <label className="row row--between" style={{ gap: 'var(--gap-sm)' }}>
          <span>
            <strong className="tiny">Keep high generations</strong>
            <div className="tiny muted">Anything at or above this. 0 turns it off.</div>
          </span>
          <input
            className="input"
            type="number"
            min={0}
            max={1000}
            style={{ width: 84 }}
            defaultValue={settings.autoLockGeneration}
            disabled={busy}
            onBlur={(event) => void set({ autoLockGeneration: Number(event.target.value) || 0 })}
          />
        </label>

        <hr className="divider" />

        <label className="row row--between" style={{ gap: 'var(--gap-sm)' }}>
          <span>
            <strong className="tiny">Advanced Breeder Mode</strong>
            <div className="tiny muted">
              Show compatibility scores, exact odds, pair bond and lineage detail. Off by default —
              none of it is needed to play.
            </div>
          </span>
          <input
            type="checkbox"
            checked={settings.advancedMode}
            disabled={busy}
            onChange={(event) => void set({ advancedMode: event.target.checked })}
          />
        </label>

        <label className="row row--between" style={{ gap: 'var(--gap-sm)' }}>
          <span>
            <strong className="tiny">Share rare hatches</strong>
            <div className="tiny muted">
              Let a Shiny or exceptional hatch be announced. Nothing is ever shared without this.
            </div>
          </span>
          <input
            type="checkbox"
            checked={settings.shareHatches}
            disabled={busy}
            onChange={(event) => void set({ shareHatches: event.target.checked })}
          />
        </label>
      </div>
    </Panel>
  );
}

/**
 * Account: how you sign in, and what your username is.
 *
 * Kept apart from the profile card above it because these two changes are not
 * the same kind of change. A bio can be edited every day; a username can be
 * changed once, ever, and other players' links to you change with it.
 */
function AccountPanel() {
  const { me, refresh } = useSession();
  const { push } = useToast();
  const [username, setUsername] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (me) setUsername(me.user.username);
  }, [me?.user.username]);

  if (!me) return null;
  const { account } = me;

  async function rename() {
    setBusy(true);
    try {
      await api.patch('/me/username', { username });
      push('success', 'Username changed.');
      void refresh();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not change your username.');
    } finally {
      setBusy(false);
    }
  }

  if (account.isGuest) {
    return (
      <Panel title="◆ Account" hint="Not claimed yet">
        <div className="stack">
          <p className="tiny">
            You're playing as <strong>{me.user.username}</strong> on a guest account. Everything works
            exactly as it does for anyone else — you just can't sign in from anywhere else yet.
          </p>
          <p className="tiny muted" style={{ margin: 0 }}>
            Guest accounts are stored on this device. Clearing your browser data or switching devices
            loses the account, and there is no recovery — claiming enables both recovery and
            cross-device play.
          </p>
          <div>
            <Link to="/claim" className="btn btn--primary">
              Claim account
            </Link>
          </div>
        </div>
      </Panel>
    );
  }

  return (
    <Panel
      title="◆ Account"
      hint={account.freeUsernameChangeAvailable ? 'One free rename available' : 'Rename used'}
    >
      <div className="stack">
        <div className="field">
          <label className="field__label" htmlFor="account-username">
            Username
          </label>
          <input
            id="account-username"
            className="input"
            value={username}
            maxLength={18}
            onChange={(event) => setUsername(event.target.value)}
            disabled={!account.freeUsernameChangeAvailable}
          />
          <span className="tiny muted">
            {account.freeUsernameChangeAvailable
              ? 'You have one free username change. Other trainers find you by this name, so pick one you will keep.'
              : 'You have used your free username change.'}
          </span>
        </div>

        {account.freeUsernameChangeAvailable && (
          <div>
            <Button
              variant="go"
              onClick={() => void rename()}
              loading={busy}
              disabled={username.trim() === me.user.username}
            >
              Change username
            </Button>
          </div>
        )}

        <p className="tiny muted" style={{ margin: 0 }}>
          {account.hasEmail
            ? 'An email address is on file for recovery.'
            : 'No email address on file — add one when password recovery arrives.'}
          {account.temporaryName && ` You started out as ${account.temporaryName}.`}
        </p>
      </div>
    </Panel>
  );
}

function NotificationsPanel() {
  const { refresh } = useSession();
  const state = useAsync<{ notifications: AppNotification[] }>(() => api.get('/me/notifications'));
  const [busy, setBusy] = useState(false);

  async function markRead() {
    setBusy(true);
    try {
      await api.post('/me/notifications/read');
      state.reload();
      void refresh();
    } finally {
      setBusy(false);
    }
  }

  const unread = (state.data?.notifications ?? []).filter((entry) => !entry.read).length;

  return (
    <Panel
      title="◆ Notifications"
      action={
        unread > 0 ? (
          <Button size="sm" onClick={() => void markRead()} loading={busy}>
            Mark all read ({unread})
          </Button>
        ) : null
      }
    >
      {state.loading ? (
        <div className="pxwell skeleton" style={{ height: 140 }} />
      ) : (state.data?.notifications ?? []).length === 0 ? (
        <EmptyState art="◌" title="Nothing yet" />
      ) : (
        <div className="stack-sm">
          {state.data!.notifications.map((notification) => (
            <div
              key={notification.id}
              className="pxwell"
              style={{
                padding: 'var(--gap-sm)',
                background: notification.read ? undefined : 'var(--paper-warm)',
              }}
            >
              <div className="row row--between">
                <span className="mono tiny">
                  {notification.title}
                  {/* A Living Trainer's notification is marked wherever it is
                      shown, not only on their profile — this is the surface
                      where a player would otherwise assume a real person. */}
                  {notification.data?.npc === true && (
                    <span className="chip chip--npc chip--sm" style={{ marginLeft: 6 }}>
                      🌿 NPC
                    </span>
                  )}
                </span>
                <span className="tiny muted">{timeAgo(notification.createdAt)}</span>
              </div>
              <p className="tiny muted" style={{ marginTop: 2 }}>{notification.body}</p>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
